// RechnungPositionen.tsx — die Positionen einer Rechnung, so wie sie im
// Access-Original nebeneinander stehen: Pos. · Vertrags-Nr. · Beitrag Kunde
// (+ bezahlt) · Beitrag Versicherer (+ bezahlt) · Storno · KaV, darunter je
// Position eine Bemerkungszeile.
//
// Bewusst KEINE Wiederverwendung der Arbeitsliste (`OffenePostenTabelle`): dort
// wird abgehakt, hier wird die Rechnung GEBAUT — Vertrag zuordnen, Beträge
// eintragen, Zeilen ergänzen und entfernen. Das sind andere Spalten und andere
// Interaktionen; eine Komponente mit einem Modus-Schalter für beides wäre an
// jeder Stelle ein Kompromiss.
//
// Deshalb Karten statt Tabellenzeilen: eine Zeile trägt sechs Eingaben plus
// Bemerkung. In einer Tabelle wären das entweder unlesbar schmale Felder oder
// ein horizontaler Scroller quer durch den Buchungsvorgang.

import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import { apiFetch } from "../../utils/apiFetch";
import { formatEuro } from "./contractRiskCells";
import { formatBetragEingabe, parseBetrag } from "./geldEingabe";
import { serverMeldung } from "./serverMeldung";
import "./contactRedesign.css";

export type Position = {
  id: number;
  invoiceId: number;
  posNo: number | null;
  contractId: number | null;
  amount: number | null;
  customerTarget: number | null;
  customerPaid: number | null;
  customerOpen: number | null;
  insurerTarget: number | null;
  insurerPaid: number | null;
  insurerOpen: number | null;
  isCancelled: boolean;
  customerPaysInsurer: boolean;
  remark: string | null;
};

export type VertragOption = {
  id: number;
  contractNbr: string | null;
  branch: string | null;
  bezeichnung?: string | null;
};

/** Rechnungskopf-Ausschnitt, den der Server nach jeder Buchung zurückgibt. */
export type KopfPatch = {
  id: number;
  status: number;
  amountCustomer: number | null;
  amountCustomerOpen: number | null;
  amountInsurer: number | null;
  amountInsurerOpen: number | null;
};

type Props = {
  invoiceId: number;
  contactId: number | null;
  positionen: Position[];
  bearbeitbar: boolean;
  /** Nutzer der Position darf löschen (eigenes Recht, nicht `bearbeitbar`). */
  darfLoeschen: boolean;
  /**
   * Meldet eine Änderung als UPDATER, nicht als fertige Liste: zwei Buchungen
   * können gleichzeitig laufen, und eine aus dem Render-Zeitpunkt des Klicks
   * berechnete Liste würde die parallel eingetroffene Änderung überschreiben.
   */
  onGeaendert: (aendere: (alt: Position[]) => Position[], kopf?: KopfPatch) => void;
};

/**
 * Betragsfeld mit Häkchen „bezahlt". Beide gehören zusammen: das Häkchen bucht
 * genau den Betrag, der daneben steht.
 *
 * Der Betrag wird beim Verlassen des Feldes gespeichert, nicht bei jedem
 * Tastendruck — sonst liefe pro Ziffer ein Schreibvorgang samt Neuberechnung
 * des Rechnungskopfs.
 */
const Geldfeld: React.FC<{
  /** Eindeutig je Feld — verknüpft Beschriftung und Eingabe (Screenreader). */
  feldId: string;
  label: string;
  betrag: number | null;
  bezahlt: boolean;
  disabled: boolean;
  bezahltDisabled: boolean;
  bezahltTitel: string;
  onBetrag: (wert: number | null) => void;
  onBezahlt: (next: boolean) => void;
}> = ({
  feldId,
  label,
  betrag,
  bezahlt,
  disabled,
  bezahltDisabled,
  bezahltTitel,
  onBetrag,
  onBezahlt,
}) => {
  const [text, setText] = useState(() => formatBetragEingabe(betrag));

  // Der Server ist die Wahrheit: nach einer Buchung (oder einem Rundungs-
  // ergebnis) muss das Feld den gespeicherten Wert zeigen, nicht den getippten.
  useEffect(() => {
    setText(formatBetragEingabe(betrag));
  }, [betrag]);

  return (
    <div className="inv-geldfeld">
      <label htmlFor={feldId}>{label}</label>
      <div className="inv-geldzeile">
        <input
          id={feldId}
          className="inp geld"
          inputMode="decimal"
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            const wert = parseBetrag(text);
            // Unlesbares NICHT als 0 durchreichen: ein Tippfehler würde sonst den
            // Beitrag der Position löschen und den Rechnungskopf neu rechnen.
            // Stattdessen zurück auf den gespeicherten Wert.
            if (wert === null && text.trim() !== "") {
              setText(formatBetragEingabe(betrag));
              return;
            }
            if (wert !== betrag) onBetrag(wert);
            else setText(formatBetragEingabe(betrag));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
        />
        <label className="inv-bezahlt" title={bezahltTitel}>
          <input
            type="checkbox"
            checked={bezahlt}
            disabled={bezahltDisabled}
            onChange={(e) => onBezahlt(e.target.checked)}
          />
          bezahlt
        </label>
      </div>
    </div>
  );
};

const RechnungPositionen: React.FC<Props> = ({
  invoiceId,
  contactId,
  positionen,
  bearbeitbar,
  darfLoeschen,
  onGeaendert,
}) => {
  const [vertraege, setVertraege] = useState<VertragOption[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState(false);

  // Verträge des Kunden — die Auswahlliste jeder Position.
  useEffect(() => {
    if (!contactId) {
      setVertraege([]);
      return;
    }
    const ctrl = new AbortController();
    apiFetch<VertragOption[]>(`contracts/by-contact/${contactId}?limit=300`, {
      signal: ctrl.signal,
    })
      .then(({ data }) => setVertraege(data ?? []))
      .catch(() => {
        if (!ctrl.signal.aborted) setVertraege([]);
      });
    return () => ctrl.abort();
  }, [contactId]);

  /** Ersetzt eine Position lokal — kein Reload, die Scroll-Position bleibt. */
  const patcheLokal = useCallback(
    (position: Position, kopf?: KopfPatch) => {
      onGeaendert(
        (alt) => alt.map((p) => (p.id === position.id ? { ...p, ...position } : p)),
        kopf,
      );
    },
    [onGeaendert],
  );

  const melde = useCallback((e: unknown, standard: string) => {
    setFehler(serverMeldung(e, standard));
  }, []);

  /** Stammdaten/Beträge einer Position (PATCH …/:id). */
  const speichern = useCallback(
    async (id: number, patch: Record<string, unknown>) => {
      setBeschaeftigt(true);
      setFehler(null);
      try {
        const res = await apiFetch<{ position: Position; invoice: KopfPatch }>(
          `invoice-positions/${id}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
          },
        );
        if (res.data?.position) patcheLokal(res.data.position, res.data.invoice);
      } catch (e) {
        melde(e, "Die Position konnte nicht gespeichert werden.");
      } finally {
        setBeschaeftigt(false);
      }
    },
    [patcheLokal, melde],
  );

  /** Zahlungsschalter (PATCH …/:id/flags) — der Server lehnt Doppelbuchungen ab. */
  const buchen = useCallback(
    async (id: number, patch: Record<string, boolean>) => {
      setBeschaeftigt(true);
      setFehler(null);
      try {
        const res = await apiFetch<{ position: Position; invoice: KopfPatch }>(
          `invoice-positions/${id}/flags`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
          },
        );
        if (res.data?.position) patcheLokal(res.data.position, res.data.invoice);
      } catch (e) {
        melde(e, "Die Buchung war nicht möglich.");
      } finally {
        setBeschaeftigt(false);
      }
    },
    [patcheLokal, melde],
  );

  const neuePosition = useCallback(async () => {
    setBeschaeftigt(true);
    setFehler(null);
    try {
      const res = await apiFetch<{ position: Position; invoice: KopfPatch }>("invoice-positions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invoiceId, amount: 0 }),
      });
      const neu = res.data?.position;
      if (neu) onGeaendert((alt) => [...alt, neu], res.data?.invoice);
    } catch (e) {
      melde(e, "Die Position konnte nicht angelegt werden.");
    } finally {
      setBeschaeftigt(false);
    }
  }, [invoiceId, onGeaendert, melde]);

  const entfernen = useCallback(
    async (id: number) => {
      setBeschaeftigt(true);
      setFehler(null);
      try {
        const res = await apiFetch<{ invoice: KopfPatch }>(`invoice-positions/${id}`, {
          method: "DELETE",
        });
        onGeaendert((alt) => alt.filter((p) => p.id !== id), res.data?.invoice);
      } catch (e) {
        melde(e, "Die Position konnte nicht entfernt werden.");
      } finally {
        setBeschaeftigt(false);
      }
    },
    [onGeaendert, melde],
  );

  return (
    <div className="inv-pos">
      <div className="inv-pos-kopf">
        <h2>Positionen{positionen.length > 0 ? ` (${positionen.length})` : ""}</h2>
        {bearbeitbar && (
          <button className="inv-btn" onClick={neuePosition} disabled={beschaeftigt}>
            + Neue Position
          </button>
        )}
      </div>

      {fehler && <div className="rd-error">{fehler}</div>}

      {positionen.length === 0 && (
        <div className="inv-pos-leer">
          Noch keine Positionen. Eine Rechnung braucht mindestens eine Position, bevor sie gebucht
          werden kann.
        </div>
      )}

      {positionen.map((p) => {
        // Sperren wie im Access-Original: KaV nimmt die Position aus dem
        // Geldfluss, Storno ebenso — beides schliesst das Buchen aus, und
        // umgekehrt sperrt eine erfolgte VR-Überweisung KaV und Storno.
        const ausserGeldfluss = p.isCancelled || p.customerPaysInsurer;
        const anVersichererGezahlt = (p.insurerPaid ?? 0) !== 0;
        // „bezahlt" heisst: es ist etwas gebucht — oder das Soll ist ausgeglichen.
        // Der frühere Test `soll > 0` liess Gutschriften (negatives Soll) dauerhaft
        // ungehakt: das Häkchen war dann nicht mehr abwählbar, die Zahlung nicht
        // rücknehmbar.
        const ausgeglichen = (soll: number | null, offen: number | null, bezahlt: number | null) =>
          (bezahlt ?? 0) !== 0 || ((soll ?? 0) !== 0 && (offen ?? 0) === 0);
        const kundeBezahlt = ausgeglichen(p.customerTarget, p.customerOpen, p.customerPaid);
        const vrBezahlt = ausgeglichen(p.insurerTarget, p.insurerOpen, p.insurerPaid);
        const gezahlt = (p.customerPaid ?? 0) !== 0 || anVersichererGezahlt;

        return (
          <div key={p.id} className={`inv-poskarte${p.isCancelled ? " storniert" : ""}`}>
            <div className="inv-pos-zeile">
              <div className="inv-pos-nr">
                {String(p.posNo ?? 0).padStart(2, "0")}
              </div>

              <div className="inv-feld">
                <label htmlFor={`pos-vertrag-${p.id}`}>Vertrags-Nr.</label>
                <select
                  id={`pos-vertrag-${p.id}`}
                  className="sel"
                  value={p.contractId ?? ""}
                  disabled={!bearbeitbar}
                  onChange={(e) =>
                    speichern(p.id, { contractId: e.target.value ? Number(e.target.value) : null })
                  }
                >
                  {/* Eine Position ohne Vertrag ist gültig — im Altbestand gibt
                      es Klärfälle, die trotzdem eine Rechnungszeile sind. */}
                  <option value="">— kein Vertrag —</option>
                  {vertraege.map((v) => (
                    <option key={v.id} value={v.id}>
                      {[v.contractNbr, v.branch].filter(Boolean).join(" · ") || `Vertrag ${v.id}`}
                    </option>
                  ))}
                </select>
              </div>

              <Geldfeld
                feldId={`pos-kunde-${p.id}`}
                label="Beitrag Kunde"
                betrag={p.customerTarget}
                bezahlt={kundeBezahlt}
                disabled={!bearbeitbar || ausserGeldfluss}
                bezahltDisabled={!bearbeitbar || ausserGeldfluss}
                bezahltTitel="Zahlung des Kunden erhalten"
                onBetrag={(wert) => speichern(p.id, { customerTarget: wert ?? 0 })}
                onBezahlt={(next) => buchen(p.id, { customerPaid: next })}
              />

              <Geldfeld
                feldId={`pos-vr-${p.id}`}
                label="Beitrag Versicherer"
                betrag={p.insurerTarget}
                bezahlt={vrBezahlt}
                disabled={!bearbeitbar || ausserGeldfluss}
                bezahltDisabled={!bearbeitbar || ausserGeldfluss}
                bezahltTitel="An den Versicherer überwiesen"
                onBetrag={(wert) => speichern(p.id, { insurerTarget: wert ?? 0 })}
                onBezahlt={(next) => buchen(p.id, { insurerPaid: next })}
              />

              <div className="inv-pos-flags">
                <label className="inv-bezahlt" title="Position stornieren">
                  <input
                    type="checkbox"
                    checked={p.isCancelled}
                    disabled={!bearbeitbar || p.customerPaysInsurer || anVersichererGezahlt}
                    onChange={(e) => buchen(p.id, { isCancelled: e.target.checked })}
                  />
                  Storno
                </label>
                <label className="inv-bezahlt" title="Kunde zahlt direkt an den Versicherer">
                  <input
                    type="checkbox"
                    checked={p.customerPaysInsurer}
                    disabled={!bearbeitbar || p.isCancelled || anVersichererGezahlt}
                    onChange={(e) => buchen(p.id, { customerPaysInsurer: e.target.checked })}
                  />
                  KaV
                </label>
                {darfLoeschen && (
                  <button
                    className="inv-pos-x"
                    // Gezahlt heisst: die Zeile ist Teil der Geldgeschichte.
                    // Sie wird storniert, nicht entfernt.
                    disabled={beschaeftigt || gezahlt}
                    title={
                      gezahlt
                        ? "Position mit gebuchter Zahlung — bitte stornieren statt entfernen"
                        : "Position entfernen"
                    }
                    onClick={() => entfernen(p.id)}
                  >
                    Entfernen
                  </button>
                )}
              </div>
            </div>

            <div className="inv-pos-bemerkung">
              <input
                className="inp"
                placeholder="Bemerkung zur Position"
                defaultValue={p.remark ?? ""}
                disabled={!bearbeitbar}
                onBlur={(e) => {
                  const wert = e.target.value.trim();
                  if (wert !== (p.remark ?? "")) speichern(p.id, { remark: wert || null });
                }}
              />
            </div>

            {/* Offene Beträge stehen klein unter der Zeile: sie sind abgeleitet,
                nicht eingebbar — sichtbar müssen sie trotzdem sein. */}
            {!ausserGeldfluss && ((p.customerOpen ?? 0) > 0 || (p.insurerOpen ?? 0) > 0) && (
              <div className="rd-summen" style={{ borderTop: "none", padding: "0 2px" }}>
                {(p.customerOpen ?? 0) > 0 && (
                  <span>
                    Kunde offen{" "}
                    <strong className="txt-danger">{formatEuro(p.customerOpen)}</strong>
                  </span>
                )}
                {(p.insurerOpen ?? 0) > 0 && (
                  <span>
                    Versicherer offen{" "}
                    <strong className="txt-warn">{formatEuro(p.insurerOpen)}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RechnungPositionen;
